import React, { useState, useEffect, useRef, useMemo } from "react";
import { socket } from "../socket";
import { useGameTimer } from "../gameTimer";
import { PlayerAvatar, PlayerTag } from "./PlayerIdentity";
import { t } from "../i18n";
import { useSounds, useCountdownTick } from "../sound";
import { alertDialog } from "../dialog";
import { btPlayer, load, fadeOut, stop } from "../btAudio";

// Pseudo-random bar heights, but stable for a given song
function waveHeights(seed, count = 28) {
  const h = [];
  let x = (seed + 1) * 2654435761 % 2147483647;
  for (let i = 0; i < count; i++) {
    x = (x * 16807) % 2147483647;
    h.push(25 + (x % 70));
  }
  return h;
}

const PART_LABEL = { title: "blindtest.partTitle", artist: "blindtest.partArtist", media: "blindtest.partMedia" };
const foundSome = f => !!(f.title || f.artist || f.media);
// Un appui sur un bouton ne doit pas retirer le focus du champ : sur un
// téléphone, le clavier se refermerait.
const keepFocus = e => e.preventDefault();

// `variant` : « classique », ou « interruption » — on buzze, la musique
// s'arrête pour tous, et seul celui qui a buzzé répond.
function BlindTest({ gameId, player, variant: initialVariant = "classique" }) {
  const [variant, setVariant]     = useState(initialVariant);
  const [total, setTotal]         = useState(0);
  const [songIndex, setSongIndex] = useState(0);
  const [mediaUrl, setMediaUrl]   = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [startRatio, setStartRatio] = useState(null); // départ en plein morceau (0..1), sinon null
  const [duration, setDuration]   = useState(30000);
  // Les cases à deviner pour cette musique, parmi titre, artiste et média :
  // seules celles que la fiche remplit (un jeu vidéo n'a pas d'artiste, une
  // musique de film n'a parfois que son média).
  const [parts, setParts]         = useState(["title", "artist"]);
  const [theme, setTheme]         = useState(null);    // thème de la musique, si le salon l'affiche
  const [progress, setProgress]   = useState(0);       // 0..1 within the current song
  const [players, setPlayers]     = useState([]);      // leaderboard
  const [history, setHistory]     = useState([]);      // past songs
  const [bubbles, setBubbles]     = useState([]);      // finds on the current song
  const [found, setFound]         = useState({});      // part -> valeur trouvée
  const [guess, setGuess]         = useState("");
  const [wrongFlash, setWrongFlash] = useState(false);
  const [reveal, setReveal]       = useState(null);    // { title, artist, media } between two songs
  const [needsClick, setNeedsClick] = useState(false); // autoplay blocked by the browser
  // Interruption
  const [paused, setPaused]       = useState(false);   // quelqu'un répond, la musique attend
  const [buzz, setBuzz]           = useState(null);    // { playerId, pseudo, until }
  const [lockedUntil, setLockedUntil] = useState(0);
  const [answer, setAnswer]       = useState("");      // la saisie de celui qui a buzzé
  const [now, setNow]             = useState(Date.now());
  const audioRef = useRef(btPlayer(0));
  const inputRef = useRef(null);

  const myId = player?.playerId;
  const { playSuccess, playFail } = useSounds();

  // ── Socket ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    socket.on("btState", (s) => {
      if (s.variant) setVariant(s.variant);
      setPaused(!!s.paused); setBuzz(s.buzz || null); setLockedUntil(s.lockedUntil || 0);
      setTotal(s.total); setSongIndex(s.index); setMediaUrl(s.mediaUrl);
      setStartedAt(s.startedAt); setDuration(s.duration); setStartRatio(s.startRatio ?? null);
      setParts(s.parts || []);
      setTheme(s.theme || null);
      setPlayers(s.players); setHistory(s.history); setBubbles(s.bubbles || []);
      setFound(s.variant === "interruption" ? (s.claimed || {}) : (s.myFound || {}));
      setReveal(null);
    });
    socket.on("btSongStart", ({ variant, index, total, mediaUrl, startedAt, startRatio, duration, parts, theme }) => {
      if (variant) setVariant(variant);
      setPaused(false); setBuzz(null); setLockedUntil(0); setAnswer("");
      setSongIndex(index); setTotal(total); setMediaUrl(mediaUrl);
      setStartedAt(startedAt); setDuration(duration); setStartRatio(startRatio ?? null);
      setParts(parts || []);
      setTheme(theme || null);
      setBubbles([]); setFound({});
      setReveal(null); setGuess("");
      inputRef.current?.focus();
    });
    socket.on("btFound", (b) => {
      setBubbles(prev => [...prev, b]);
      if (b.players) setPlayers(b.players);
    });
    socket.on("btGuessResult", (r) => {
      if (!r.correct) {
        playFail();
        setWrongFlash(true);
        setTimeout(() => setWrongFlash(false), 500);
        return;
      }
      playSuccess();
      // Une saisie peut valider plusieurs cases d'un coup.
      setFound(prev => ({ ...prev, ...(r.parts || { [r.correct]: r.value }) }));
    });
    socket.on("btSongEnd", ({ title, artist, media, players, history }) => {
      setReveal({ title, artist, media });
      setPlayers(players); setHistory(history);
      setStartedAt(null);
    });
    socket.on("btError", ({ error }) => alertDialog(error));
    // Interruption : un buzz coupe la musique net, pour tout le monde.
    socket.on("btBuzzed", ({ buzz }) => {
      stop(audioRef.current);
      setPaused(true); setBuzz(buzz);
    });
    socket.on("btBuzzResult", (r) => {
      setBuzz(null);
      setFound(prev => ({ ...prev, ...r.hits }));
      if (r.players) setPlayers(r.players);
      if (r.playerId === myId) {
        (Object.keys(r.hits).length ? playSuccess : playFail)();
        setLockedUntil(r.lockedUntil || 0);
        setAnswer("");
      }
      if (r.resume) { setPaused(false); setStartedAt(r.startedAt); }
    });

    socket.emit("btGetState", { gameId });
    return () => {
      ["btState", "btSongStart", "btFound", "btGuessResult", "btSongEnd", "btError", "btBuzzed", "btBuzzResult"]
        .forEach(e => socket.off(e));
    };
  }, [gameId, myId, playSuccess, playFail]);

  // ── Song progress ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!startedAt) return;
    const iv = setInterval(() => {
      setNow(Date.now());
      if (!paused) setProgress(Math.min(1, (Date.now() - startedAt) / duration));
    }, 200);
    return () => clearInterval(iv);
  }, [startedAt, duration, paused]);

  // ── Synchronised audio playback ────────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !mediaUrl || !startedAt || paused) return;
    load(audio, mediaUrl);
    // En plein morceau : entre 25 % et 60 % du fichier, sans que l'écoute
    // déborde de la fin. Seul le client connaît la durée réelle du fichier.
    const seek = () => {
      const elapsed = Math.max(0, (Date.now() - startedAt) / 1000);
      const dur = Number.isFinite(audio.duration) ? audio.duration : 0;
      const base = startRatio === null || !dur ? 0
        : Math.max(0, Math.min(dur * (0.25 + 0.35 * startRatio), dur - duration / 1000));
      audio.currentTime = base + elapsed;
    };
    if (startRatio === null || audio.readyState >= 1) seek();
    else audio.addEventListener("loadedmetadata", seek, { once: true });
    audio.play().then(() => setNeedsClick(false)).catch(() => setNeedsClick(true));
    return () => audio.removeEventListener("loadedmetadata", seek);
  }, [mediaUrl, startedAt, startRatio, duration, paused]);

  // La fin de l'écoute s'éteint en douceur, même quand tout le monde a trouvé.
  useEffect(() => {
    if (reveal) fadeOut(audioRef.current);
  }, [reveal]);
  useEffect(() => () => stop(audioRef.current), []);

  // Son bloqué par le navigateur : le prochain toucher, où qu'il soit, le relance.
  useEffect(() => {
    if (!needsClick) return undefined;
    const retry = () => audioRef.current?.play().then(() => setNeedsClick(false)).catch(() => {});
    window.addEventListener("pointerdown", retry);
    window.addEventListener("keydown", retry);
    return () => { window.removeEventListener("pointerdown", retry); window.removeEventListener("keydown", retry); };
  }, [needsClick]);

  // ── Interruption ───────────────────────────────────────────────────────────
  const isInt = variant === "interruption";
  const myBuzz = isInt && buzz?.playerId === myId;
  const locked = lockedUntil > now;
  const canBuzz = isInt && !!startedAt && !reveal && !buzz && !locked && !parts.every(k => found[k]);
  const doBuzz = () => {
    if (!canBuzz) return;
    // Le focus se donne pendant le geste : c'est ce qui ouvre le clavier.
    inputRef.current?.focus();
    socket.emit("btBuzz", { gameId });
  };
  const submitBuzz = () => { if (myBuzz) socket.emit("btBuzzAnswer", { gameId, answers: { text: answer } }); };

  const sendGuess = () => {
    const text = guess.trim();
    if (!text || reveal || !startedAt) return;
    socket.emit("btGuess", { gameId, text });
    setGuess("");
  };

  const heights = useMemo(() => waveHeights(songIndex), [songIndex]);
  // Pendant qu'on répond (Interruption), l'écoute est figée à l'instant du buzz.
  const clock = paused && buzz?.at ? buzz.at : Date.now();
  const remaining = startedAt ? Math.max(0, Math.ceil((duration - (clock - startedAt)) / 1000)) : 0;
  const allFound = parts.length > 0 && parts.every(k => found[k]);
  const placeholder = allFound ? t("blindtest.allFound")
    : parts.join() === "title" ? t("blindtest.guessTitle")
    : parts.join() === "title,artist" ? t("blindtest.guessBoth")
    : t("blindtest.guessAny");

  useCountdownTick(remaining, { active: !!startedAt && !reveal && !paused });
  useGameTimer(startedAt ? { remaining, duration, idle: !!reveal || paused } : null);

  // Status of the progress dots at the top:
  // green = found (at least one element), white = in progress, black = otherwise
  const dotClass = (i) => {
    if (i === songIndex && !reveal) return "current";
    const h = history[i];
    if (h && h.finders.some(f => f.playerId === myId && foundSome(f))) return "found";
    return "";
  };

  return (
    <div className="BlindTest">
      {/* Representation of the whole game */}
      <div className="bt-topbar">
        <div className="bt-dots">
          {Array.from({ length: total }, (_, i) => (
            <span key={i} className={`bt-dot ${dotClass(i)}`}></span>
          ))}
        </div>
      </div>

      <div className="bt-layout">
        {/* Leaderboard */}
        <aside className="bt-panel bt-left">
          <h2 className="bt-panel-title">{t("blindtest.ranking")}</h2>
          <ul className="bt-ranking">
            {players.map((p, i) => (
              <li key={p.playerId}>
                <PlayerTag player={p} pos={i + 1} score={p.score}
                  className={`${p.playerId === myId ? "me" : ""}${p.offline ? " offline" : ""}`} />
              </li>
            ))}
          </ul>
        </aside>

        {/* Central area: waveform + answers + input */}
        <main className="bt-center">
          {theme && <span className="bt-theme">{theme}</span>}
          <div className="bt-wave-wrap">
            <div className="bt-wave">
              {heights.map((h, i) => (
                <span key={i} className="bt-bar" style={{ height: `${h}%` }}></span>
              ))}
              <div className="bt-wave-progress" style={{ clipPath: `inset(0 ${(1 - progress) * 100}% 0 0)` }}>
                {heights.map((h, i) => (
                  <span key={i} className="bt-bar filled" style={{ height: `${h}%` }}></span>
                ))}
              </div>
              {bubbles.map((b, i) => (
                <div key={i}
                  className={`bt-bubble${b.both ? " full" : " half"}`}
                  style={{ left: `${b.at * 100}%`, top: `${8 + (i % 3) * 26}px` }}
                  title={`${b.pseudo} — ${t(PART_LABEL[b.part] || "blindtest.partTitle")}`}>
                  {/* Le joueur du classement porte ses cosmétiques : sans photo,
                      ce sont ses initiales, pas un rond blanc. */}
                  <PlayerAvatar player={players.find(p => p.playerId === b.playerId) || b} size={b.both ? 40 : 34} />
                </div>
              ))}
            </div>
            <div className="bt-wave-track">
              <div className="bt-wave-track-fill" style={{ clipPath: `inset(0 ${(1 - progress) * 100}% 0 0)` }}></div>
            </div>
          </div>

          {reveal ? (
            <div className="bt-reveal">
              <span className="bt-reveal-label">{t("blindtest.itWas")}</span>
              <span className="bt-reveal-value">{[reveal.artist, reveal.title].filter(Boolean).join(" — ") || reveal.media}</span>
              {reveal.media && (reveal.artist || reveal.title) && <span className="bt-reveal-media">{reveal.media}</span>}
            </div>
          ) : (
            <div className="bt-found-row">
              {["artist", "title", "media"].filter(k => parts.includes(k)).map(k => (
                <span key={k} className="bt-found-item">{t(`blindtest.${k}`)} <b className={found[k] ? "ok" : ""}>{found[k] || ""}</b></span>
              ))}
            </div>
          )}

          {/* Le champ n'est jamais désactivé : il garderait sinon le clavier
              d'un téléphone fermé à chaque nouvelle musique. */}
          {isInt ? (
            <div className="bt-int">
              {/* Une seule saisie : elle peut nommer titre, artiste et média à la fois. */}
              <div className="bt-input-row bt-int-fields">
                <input ref={inputRef}
                  className={myBuzz ? "bt-int-live" : ""}
                  value={answer}
                  placeholder={placeholder}
                  onChange={e => setAnswer(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && (myBuzz ? submitBuzz() : doBuzz())} />
              </div>
              {myBuzz ? (
                <button className="btn btn-primary bt-buzz" onPointerDown={keepFocus} onClick={submitBuzz}>
                  {t("blindtest.answerNow", { s: Math.max(0, Math.ceil((buzz.until - now) / 1000)) })}
                </button>
              ) : (
                <button className="btn btn-primary bt-buzz" onPointerDown={keepFocus} onClick={doBuzz} disabled={!canBuzz}>
                  {buzz ? t("blindtest.someoneAnswers", { name: buzz.pseudo })
                    : locked ? t("blindtest.buzzLocked", { s: Math.ceil((lockedUntil - now) / 1000) })
                    : t("blindtest.buzz")}
                </button>
              )}
            </div>
          ) : (
            <div className="bt-input-row">
              <input
                ref={inputRef}
                className={`${wrongFlash ? "wrong" : ""}${!!reveal || !startedAt || allFound ? " idle" : ""}`}
                value={guess}
                placeholder={placeholder}
                onChange={e => setGuess(e.target.value)}
                onKeyDown={e => e.key === "Enter" && sendGuess()}
                autoFocus
              />
              <button className="btn btn-primary bt-send" onPointerDown={keepFocus} onClick={sendGuess}
                disabled={!!reveal || !startedAt || allFound}>
                {t("blindtest.send")}
              </button>
            </div>
          )}

          {needsClick && (
            <button className="btn bt-unmute" onClick={() => {
              audioRef.current?.play().then(() => setNeedsClick(false)).catch(() => {});
            }}>
              {t("blindtest.enableSound")}
            </button>
          )}
        </main>

        {/* History */}
        <aside className="bt-panel bt-right">
          <h2 className="bt-panel-title">{t("blindtest.history")}</h2>
          <ul className="bt-history">
            {history.map((h, i) => {
              const mine = h.finders.find(f => f.playerId === myId);
              const foundByMe = mine && foundSome(mine);
              // Artiste, titre, média : la première case remplie en gras.
              const [head, ...rest] = [h.artist, h.title, h.media].filter(Boolean);
              return (
                <li key={i} className="bt-history-row">
                  <span className={`bt-history-dot${foundByMe ? " found" : ""}`}></span>
                  <div className="bt-history-text">
                    <b>{head}</b>
                    {rest.map((v, j) => <span key={j}>{v}</span>)}
                  </div>
                  <div className="bt-history-finders">
                    {h.finders.filter(foundSome).map(f => {
                      const fp = players.find(p => p.playerId === f.playerId);
                      return fp ? <span key={f.playerId} title={fp.pseudo}><PlayerAvatar player={fp} size={22} /></span> : null;
                    })}
                  </div>
                </li>
              );
            })}
            {history.length === 0 && <li className="bt-history-empty">{t("blindtest.emptyHistory")}</li>}
          </ul>
        </aside>
      </div>
    </div>
  );
}

export default BlindTest;
